import { useState, useEffect, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import { RiCloseLargeFill } from "react-icons/ri";
import DynamicSelect from "../../DynamicSelect";
import { PrintBillLayout } from "../../PrintBillLayout";

const API_URL = "https://api.muktihospital.com/api/ambulance-call";
const PATIENT_API_URL = "https://api.muktihospital.com/api/patient";
const PATIENT_ADD_API_URL = "https://api.muktihospital.com/api/patient/add";
const AMBULANCE_API_URL = "https://api.muktihospital.com/api/ambulance";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const RequiredIcon = () => <span className="text-red-600">*</span>;

const AmbulanceCallForm = ({ isOpen, onClose, onSuccess, initialData, callId }) => {
  const [formData, setFormData] = useState({
    id: "",
    vehicleModel: "",
    driverName: "",
    standardCharge: "0",
    discount: "0",
    tax: "0",
    date: new Date().toISOString().split("T")[0],
    caseId: "",
    patientId: "",
    patientName: "",
    phoneNumber: "",
    note: "",
    paymentMode: "cash",
    paymentAmount: "0",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    total: 0,
    discountAmount: 0,
    taxAmount: 0,
    netAmount: 0,
  });

  const [files, setFiles] = useState({
    attachment: null,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [patients, setPatients] = useState([]);
  const [caseIds, setCaseIds] = useState([]);
  const [filteredCaseIds, setFilteredCaseIds] = useState([]);
  const [showCaseIdSuggestions, setShowCaseIdSuggestions] = useState(false);
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    name: "",
    phoneNumber: "",
  });

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);

  const caseIdInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  const calculateAmounts = (data) => {
    const standardCharge = parseFloat(data.standardCharge) || 0;
    const discountPercent = parseFloat(data.discount) || 0;
    const taxPercent = parseFloat(data.tax) || 0;

    const discountAmount = (standardCharge * discountPercent) / 100;
    const subtotal = standardCharge - discountAmount;
    const taxAmount = (subtotal * taxPercent) / 100;
    const netAmount = subtotal + taxAmount;

    return {
      total: Math.round(standardCharge),
      discountAmount: Math.round(discountAmount),
      taxAmount: Math.round(taxAmount),
      netAmount: Math.round(netAmount),
    };
  };

  useEffect(() => {
    const amounts = calculateAmounts(formData);
    setCalculatedValues(amounts);
  }, [formData.standardCharge, formData.discount, formData.tax]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vehiclesResp, patientsResp, callsResp] = await Promise.all([
          axios.get(AMBULANCE_API_URL, { headers: { "x-api-key": API_KEY } }),
          axios.get(PATIENT_API_URL, { headers: { "x-api-key": API_KEY } }),
          axios.get(API_URL, { headers: { "x-api-key": API_KEY } }),
        ]);

        const vehiclesData = Array.isArray(vehiclesResp.data) ? vehiclesResp.data : [];
        setVehicleModels(vehiclesData);

        const patientsData = Array.isArray(patientsResp.data) ? patientsResp.data : [];
        setPatients(patientsData);

        const callsData = Array.isArray(callsResp.data) ? callsResp.data : [];
        const caseIdOptions = callsData
          .filter((item) => item.caseId)
          .map((item) => ({
            id: item.id,
            caseId: item.caseId,
          }));
        setCaseIds(caseIdOptions);
        setFilteredCaseIds(caseIdOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data.", { autoClose: 2000 });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isOpen && (callId || initialData)) {
      const loadCall = async () => {
        setIsLoading(true);
        try {
          const response = initialData
            ? { data: initialData }
            : await axios.get(`${API_URL}/${callId}`, {
                headers: { "x-api-key": API_KEY },
              });
          const call = response.data;
          const updatedFormData = {
            id: call.id || "",
            vehicleModel: call.vehicleModel?.trim() || "",
            driverName: call.driverName?.trim() || "",
            standardCharge: call.standardCharge?.toString() || "0",
            discount: call.discount?.toString() || "0",
            tax: call.tax?.toString() || "0",
            date: call.date ? call.date.split("T")[0] : new Date().toISOString().split("T")[0],
            caseId: call.caseId || "",
            patientId: call.patientId || "", 
            patientName: call.patientName?.trim() || "",
            phoneNumber: call.phoneNumber || "",
            note: call.note || "",
            paymentMode: call.paymentMode || "cash",
            paymentAmount: call.paymentAmount?.toString() || "0",
          };
          setFormData(updatedFormData);
          const calculated = call.total !== undefined && call.netAmount !== undefined
            ? {
                total: Math.round(call.total),
                discountAmount: Math.round(call.discountAmount || 0),
                taxAmount: Math.round(call.taxAmount || 0),
                netAmount: Math.round(call.netAmount),
              }
            : calculateAmounts(updatedFormData);
          setCalculatedValues(calculated);
        } catch (error) {
          console.error("Error loading call:", error);
          toast.error("Failed to load call data.", { autoClose: 2000 });
        } finally {
          setIsLoading(false);
        }
      };
      loadCall();
    } else if (isOpen) {
      handleReset();
    }
  }, [isOpen, callId, initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCaseIdChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, caseId: value, id: "" }));
    setShowCaseIdSuggestions(true);
    setErrors((prev) => ({ ...prev, caseId: "" }));

    if (value.trim() === "") {
      setFilteredCaseIds(caseIds);
    } else {
      const filtered = caseIds.filter((item) =>
        item.caseId.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCaseIds(filtered);
    }
  };

  const handleCaseIdSelect = async (selectedCase) => {
    setIsLoading(true);
    setShowCaseIdSuggestions(false);
    try {
      const response = await axios.get(`${API_URL}/${selectedCase.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const call = response.data;
      const updatedFormData = {
        id: call.id || "",
        vehicleModel: call.vehicleModel?.trim() || "",
        driverName: call.driverName?.trim() || "",
        standardCharge: call.standardCharge?.toString() || "0",
        discount: call.discount?.toString() || "0",
        tax: call.tax?.toString() || "0",
        date: call.date ? call.date.split("T")[0] : new Date().toISOString().split("T")[0],
        caseId: call.caseId || "",
        patientId: call.patientId || "",
        patientName: call.patientName?.trim() || "",
        phoneNumber: call.phoneNumber || "",
        note: call.note || "",
        paymentMode: call.paymentMode || "cash",
        paymentAmount: call.paymentAmount?.toString() || "0",
      };
      setFormData(updatedFormData);
      const calculated = call.total !== undefined && call.netAmount !== undefined
        ? {
            total: Math.round(call.total),
            discountAmount: Math.round(call.discountAmount || 0),
            taxAmount: Math.round(call.taxAmount || 0),
            netAmount: Math.round(call.netAmount),
          }
        : calculateAmounts(updatedFormData);
      setCalculatedValues(calculated);
      toast.info("Call details loaded.", { autoClose: 2000 });
    } catch (error) {
      console.error("Error fetching case data:", error);
      toast.error("Failed to load case data.", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientSelect = (selectedOption) => {
    if (selectedOption) {
      const p = selectedOption.data;

      setFormData((prev) => ({
        ...prev,
        patientId: p.id,
        patientName: p.name,
        phoneNumber: p.phoneNumber || "",
      }));
      setErrors((prev) => ({ ...prev, patientName: "" }));
    } else {
      setFormData((prev) => ({
        ...prev,
        patientId: "",
        patientName: "",
        phoneNumber: "",
      }));
    }
  };

  const handleSelectChange = (name) => (selectedOption) => {
    if (name === "vehicleModel") {
      const selectedVehicle = vehicleModels.find((v) => v.vehicleNumber === selectedOption?.value);
      setFormData((prev) => ({
        ...prev,
        vehicleModel: selectedOption?.value || "",
        driverName: selectedVehicle?.driverName || "",
      }));
      setErrors((prev) => ({ ...prev, vehicleModel: "", driverName: "" }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: selectedOption?.value || "",
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    setFiles({ ...files, [e.target.name]: e.target.files[0] });
    setErrors((prev) => ({ ...prev, attachment: "" }));
  };

  const handleNewPatientChange = (e) => {
    const { name, value } = e.target;
    setNewPatientData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateNewPatient = async () => {
    const newErrors = {};
    if (!newPatientData.name) newErrors.name = "Patient name is required";
    if (!newPatientData.phoneNumber) newErrors.phoneNumber = "Phone number is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields.", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    try {
      let formattedPhoneNumber = newPatientData.phoneNumber.trim();
      if (!formattedPhoneNumber.startsWith("88")) {
        formattedPhoneNumber = "88" + formattedPhoneNumber.replace(/^0+/, "");
      }

      const patientToCreate = {
        name: newPatientData.name,
        phoneNumber: formattedPhoneNumber,
      };

      const response = await axios.post(PATIENT_ADD_API_URL, patientToCreate, {
        headers: { "x-api-key": API_KEY },
      });

      const patientsResp = await axios.get(PATIENT_API_URL, {
        headers: { "x-api-key": API_KEY },
      });
      const patientsData = Array.isArray(patientsResp.data) ? patientsResp.data : [];
      setPatients(patientsData);

      const newPatientId =
        response.data.patient?.id ||
        patientsData.find((p) => p.phoneNumber === formattedPhoneNumber)?.id;

      setFormData((prev) => ({
        ...prev,
        patientId: newPatientId || "",
        patientName: newPatientData.name,
        phoneNumber: formattedPhoneNumber,
      }));

      setShowNewPatientForm(false);
      setNewPatientData({ name: "", phoneNumber: "" });
      toast.success("New patient added successfully!", { autoClose: 2000 });
    } catch (error) {
      console.error("Error creating new patient:", error);
      toast.error("Failed to create new patient.", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      id: "",
      vehicleModel: "",
      driverName: "",
      standardCharge: "0",
      discount: "0",
      tax: "0",
      date: new Date().toISOString().split("T")[0],
      caseId: "",
      patientId: "",
      patientName: "",
      phoneNumber: "",
      note: "",
      paymentMode: "cash",
      paymentAmount: "0",
    });
    setCalculatedValues({
      total: 0,
      discountAmount: 0,
      taxAmount: 0,
      netAmount: 0,
    });
    setFiles({ attachment: null });
    setErrors({});
    setShowCaseIdSuggestions(false);
    setFilteredCaseIds(caseIds);
    setShowNewPatientForm(false);
    setNewPatientData({ name: "", phoneNumber: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.patientName) newErrors.patientName = "Patient name is required";
    if (!formData.vehicleModel) newErrors.vehicleModel = "Vehicle number is required";
    if (!formData.driverName) newErrors.driverName = "Driver name is required";
    if (!formData.standardCharge || parseFloat(formData.standardCharge) <= 0)
      newErrors.standardCharge = "Standard charge must be a positive number";
    if (!formData.paymentMode) newErrors.paymentMode = "Payment mode is required";
    if (!formData.paymentAmount || parseFloat(formData.paymentAmount) < 0)
      newErrors.paymentAmount = "Payment amount must be a non-negative number";
    if (formData.tax === "" || isNaN(parseFloat(formData.tax)) || parseFloat(formData.tax) < 0)
      newErrors.tax = "Tax must be a non-negative number";
    if (formData.discount === "" || isNaN(parseFloat(formData.discount)) || parseFloat(formData.discount) < 0)
      newErrors.discount = "Discount must be a non-negative number";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields.", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        chargeName: "Ambulance Service",
        chargeCategory: "Ambulance",
        date: new Date(formData.date).toISOString(),
      };

      const formDataForSubmit = new FormData();
      Object.entries(dataToSubmit).forEach(([key, value]) => {
        formDataForSubmit.append(key, value || "");
      });

      if (files.attachment) {
        formDataForSubmit.append("attachment", files.attachment);
      }

      console.log("Sending data to backend:", Object.fromEntries(formDataForSubmit));

      const url = formData.id ? `${API_URL}/${formData.id}` : API_URL;
      const method = formData.id ? "put" : "post";

      const response = await axios[method](url, formDataForSubmit, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-api-key": API_KEY,
        },
      });

      const savedCall = response.data;
      onSuccess(savedCall);
      toast.success(formData.id ? "Ambulance call updated successfully!" : "Ambulance call saved successfully!", {
        autoClose: 2000,
      });

      const updatedFormData = {
        id: savedCall.id || "",
        vehicleModel: savedCall.vehicleModel?.trim() || "",
        driverName: savedCall.driverName?.trim() || "",
        standardCharge: savedCall.standardCharge?.toString() || "0",
        discount: savedCall.discount?.toString() || "0",
        tax: savedCall.tax?.toString() || "0",
        date: savedCall.date ? savedCall.date.split("T")[0] : new Date().toISOString().split("T")[0],
        caseId: savedCall.caseId || "",
        patientId: savedCall.patientId || formData.patientId,
        patientName: savedCall.patientName?.trim() || "",
        phoneNumber: savedCall.phoneNumber || formData.phoneNumber,
        note: savedCall.note || "",
        paymentMode: savedCall.paymentMode || "cash",
        paymentAmount: savedCall.paymentAmount ? savedCall.paymentAmount.toString() : "0",
      };
      setFormData(updatedFormData);

      const calculated = {
        total: Math.round(savedCall.total || 0),
        discountAmount: Math.round(savedCall.discountAmount || 0),
        taxAmount: Math.round(savedCall.taxAmount || 0),
        netAmount: Math.round(savedCall.netAmount || 0),
      };
      setCalculatedValues(calculated);

      if (!formData.id) {
        handleReset();
      }
    } catch (error) {
      console.error("Error saving call:", error);
      console.error("Server response:", error.response?.data);
      setErrors({ apiError: error.response?.data?.error || "Failed to save call" });
      toast.error(`Failed to save call: ${error.response?.data?.error || "Unknown error"}`, { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndPrint = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.patientName) newErrors.patientName = "Patient name is required";
    if (!formData.vehicleModel) newErrors.vehicleModel = "Vehicle number is required";
    if (!formData.driverName) newErrors.driverName = "Driver name is required";
    if (!formData.standardCharge || parseFloat(formData.standardCharge) <= 0)
      newErrors.standardCharge = "Standard charge must be a positive number";
    if (!formData.paymentMode) newErrors.paymentMode = "Payment mode is required";
    if (!formData.paymentAmount || parseFloat(formData.paymentAmount) < 0)
      newErrors.paymentAmount = "Payment amount must be a non-negative number";
    if (formData.tax === "" || isNaN(parseFloat(formData.tax)) || parseFloat(formData.tax) < 0)
      newErrors.tax = "Tax must be a non-negative number";
    if (formData.discount === "" || isNaN(parseFloat(formData.discount)) || parseFloat(formData.discount) < 0)
      newErrors.discount = "Discount must be a non-negative number";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields.", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        chargeName: "Ambulance Service",
        chargeCategory: "Ambulance",
        date: new Date(formData.date).toISOString(),
      };

      const formDataForSubmit = new FormData();
      Object.entries(dataToSubmit).forEach(([key, value]) => {
        formDataForSubmit.append(key, value || "");
      });

      if (files.attachment) {
        formDataForSubmit.append("attachment", files.attachment);
      }

      console.log("Sending data to backend:", Object.fromEntries(formDataForSubmit));

      const url = formData.id ? `${API_URL}/${formData.id}` : API_URL;
      const method = formData.id ? "put" : "post";

      const response = await axios[method](url, formDataForSubmit, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-api-key": API_KEY,
        },
      });

      const savedCall = response.data;
      onSuccess(savedCall);
      toast.success(formData.id ? "Ambulance call updated successfully!" : "Ambulance call saved successfully!", {
        autoClose: 2000,
      });

      const updatedFormData = {
        id: savedCall.id || "",
        vehicleModel: savedCall.vehicleModel?.trim() || "",
        driverName: savedCall.driverName?.trim() || "",
        standardCharge: savedCall.standardCharge?.toString() || "0",
        discount: savedCall.discount?.toString() || "0",
        tax: savedCall.tax?.toString() || "0",
        date: savedCall.date ? savedCall.date.split("T")[0] : new Date().toISOString().split("T")[0],
        caseId: savedCall.caseId || "",
        patientId: savedCall.patientId || formData.patientId,
        patientName: savedCall.patientName?.trim() || "",
        phoneNumber: savedCall.phoneNumber || formData.phoneNumber,
        note: savedCall.note || "",
        paymentMode: savedCall.paymentMode || "cash",
        paymentAmount: savedCall.paymentAmount?.toString() || "0",
      };
      setFormData(updatedFormData);

      const calculated = {
        total: Math.round(savedCall.total || 0),
        discountAmount: Math.round(savedCall.discountAmount || 0),
        taxAmount: Math.round(savedCall.taxAmount || 0),
        netAmount: Math.round(savedCall.netAmount || 0),
      };
      setCalculatedValues(calculated);

      const billInfo = (
        <>
          <div>
            <p><strong>Case ID:</strong> {formData.caseId || "N/A"}</p>
            <p><strong>Date:</strong> {formData.date ? new Date(formData.date).toLocaleDateString("en-US") : "N/A"}</p>
            <p><strong>Patient Name:</strong> {formData.patientName || "N/A"}</p>
          </div>
          <div>
            <p><strong>Vehicle Number:</strong> {formData.vehicleModel || "N/A"}</p>
            <p><strong>Driver Name:</strong> {formData.driverName || "N/A"}</p>
            <p><strong>Payment Mode:</strong> {formData.paymentMode || "N/A"}</p>
          </div>
        </>
      );

      const table = (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 sm:p-3 border font-semibold">Charge Name</th>
              <th className="p-2 sm:p-3 border font-semibold text-right">Standard Charge (৳)</th>
              <th className="p-2 sm:p-3 border font-semibold text-right">Discount (%)</th>
              <th className="p-2 sm:p-3 border font-semibold text-right">Vat (%)</th>
              <th className="p-2 sm:p-3 border font-semibold text-right">Net Amount (৳)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t">
              <td className="p-2 sm:p-3 border">Ambulance Service</td>
              <td className="p-2 sm:p-3 border text-right">{parseFloat(formData.standardCharge || 0).toFixed(2)}</td>
              <td className="p-2 sm:p-3 border text-right">{parseFloat(formData.discount || 0).toFixed(2)}</td>
              <td className="p-2 sm:p-3 border text-right">{parseFloat(formData.tax || 0).toFixed(2)}</td>
              <td className="p-2 sm:p-3 border text-right">{parseFloat(calculated.netAmount || 0).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      );

      const summary = (
        <div className="flex flex-col text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Standard Charge:</span>
            <span>৳{parseFloat(formData.standardCharge || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Discount (%):</span>
            <span>{parseFloat(formData.discount || 0).toFixed(2)}%</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Discount Amount:</span>
            <span>৳{parseFloat(calculated.discountAmount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Vat (%):</span>
            <span>{parseFloat(formData.tax || 0).toFixed(2)}%</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Vat Amount:</span>
            <span>৳{parseFloat(calculated.taxAmount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Net Amount:</span>
            <span>৳{parseFloat(calculated.netAmount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Payment Amount:</span>
            <span>৳{parseFloat(formData.paymentAmount || 0).toFixed(2)}</span>
          </div>
        </div>
      );

      const noteSection = formData.note ? (
        <div className="mt-2 text-sm">
          <strong>Note:</strong> {formData.note}
        </div>
      ) : null;

      const newPrintData = { billInfo, table, summary, note: noteSection };
      setPrintData(newPrintData);
      setShowPrintPreview(true);

      setTimeout(async () => {
        const elementsToHide = document.querySelectorAll(".print-hidden, .print-hidden-in-print");
        elementsToHide.forEach((el) => (el.style.display = "none"));

        const printLayout = document.querySelector(".ambulance-bill-print-preview");
        if (printLayout) {
          printLayout.style.display = "block";
        }

        window.print();

        setTimeout(() => {
          elementsToHide.forEach((el) => (el.style.display = ""));
          if (printLayout) {
            printLayout.style.display = "none";
          }
          setShowPrintPreview(false);
          if (!formData.id) {
            handleReset();
          }
        }, 500);
      }, 1000);
    } catch (error) {
      console.error("Error saving call:", error);
      console.error("Server response:", error.response?.data);
      setErrors({ apiError: error.response?.data?.error || "Failed to save call" });
      toast.error(`Failed to save call: ${error.response?.data?.error || "Unknown error"}`, { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const patientOptions = patients.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.phoneNumber})`,
    data: p
  }));

  const vehicleOptions = vehicleModels.map((v) => ({
    value: v.vehicleNumber,
    label: v.vehicleNumber,
  }));

  const paymentModeOptions = [
    { value: "cash", label: "Cash" },
    { value: "Credit Card", label: "Credit Card" },
    { value: "UPI", label: "UPI" },
  ];

  const isEditing = !!formData.id || !!callId;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
      {/* <ToastContainer /> */}
      <div className="bg-white shadow-lg w-full max-w-full max-h-[100vh] h-full overflow-y-auto print-wrapper">
        <div className="bg-[#ececec] p-5 flex flex-col md:flex-row justify-between items-center mb-4 border-b border-dashed border-gray-300">
          <h2 className="text-xl font-semibold text-gray-700">
            {isEditing ? "Edit Ambulance Call" : "Add Ambulance Call"}
          </h2>
          <button
            title="Close"
            className="size-7 rounded-full bg-white text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
            onClick={onClose}
            disabled={isLoading}
          >
            <RiCloseLargeFill />
          </button>
        </div>
        <div className="my-4 px-6 h-[calc(100vh-150px)] overflow-y-auto">
          {showNewPatientForm ? (
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md mx-auto">
              <h3 className="text-lg font-medium text-black mb-4">Add New Patient</h3>
              <div className="space-y-4">
                <div>
                  <label className="label font-medium text-black">
                    Patient Name <RequiredIcon />
                  </label>
                  <input
                    name="name"
                    type="text"
                    className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                    value={newPatientData.name}
                    onChange={handleNewPatientChange}
                    ref={nameInputRef}
                    placeholder="Enter patient name"
                    disabled={isLoading}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Phone Number <RequiredIcon />
                  </label>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-3 py-2 border border-gray-300 rounded-l-md text-gray-600">88</span>
                    <input
                      name="phoneNumber"
                      type="text"
                      inputMode="numeric"
                      className={`w-full p-2 border rounded-r-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border-l-0 ${
                        errors.phoneNumber ? "border-red-500" : "border-gray-300"
                      }`}
                      value={newPatientData.phoneNumber}
                      onChange={handleNewPatientChange}
                      ref={phoneInputRef}
                      placeholder="Enter number (e.g., 01827348931)"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.phoneNumber}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Enter phone number with or without leading zero; 88 prefix will be added automatically.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowNewPatientForm(false);
                    setNewPatientData({ name: "", phoneNumber: "" });
                    setErrors({});
                  }}
                  className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNewPatient}
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Add Patient"}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="relative">
                  <label className="label font-medium text-black">
                    Case ID <RequiredIcon />
                  </label>
                  <input
                    name="caseId"
                    type="text"
                    className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.caseId ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.caseId}
                    onChange={handleCaseIdChange}
                    ref={caseIdInputRef}
                    placeholder="Search Case ID"
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  {errors.caseId && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.caseId}</p>
                  )}
                  {showCaseIdSuggestions && filteredCaseIds.length > 0 && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto mt-1">
                      {filteredCaseIds.map((item) => (
                        <li
                          key={item.id}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleCaseIdSelect(item)}
                        >
                          {item.caseId}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Date <RequiredIcon />
                  </label>
                  <DatePicker
                    selected={formData.date ? new Date(formData.date) : null}
                    onChange={(d) => {
                      setFormData((prev) => ({
                        ...prev,
                        date: d ? d.toISOString().split("T")[0] : "",
                      }));
                      setErrors((prev) => ({ ...prev, date: "" }));
                    }}
                    className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.date ? "border-red-500" : "border-gray-300"
                    }`}
                    dateFormat="yyyy-MM-dd"
                    disabled={isLoading}
                    placeholderText="Select date"
                  />
                  {errors.date && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.date}</p>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Patient Name <RequiredIcon />
                  </label>
                  <div className="flex space-x-2">
                    <div className="w-full">
                      <DynamicSelect
                        options={patientOptions}
                        value={
                          patientOptions.find((p) => p.value === formData.patientId) || null
                        }
                        onChange={handlePatientSelect}
                        isSearchable
                        placeholder="Select Patient"
                        className={`w-full rounded-md border ${
                          errors.patientName ? "border-red-500" : "border-gray-300"
                        } bg-gray-50 shadow-sm`}
                        isDisabled={isLoading}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNewPatientForm(true)}
                      className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                      disabled={isLoading}
                    >
                      Add New
                    </button>
                  </div>
                  {errors.patientName && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.patientName}</p>
                  )}
                </div>            
                <div>
                  <label className="label font-medium text-black">Attachment</label>
                  <input
                    type="file"
                    name="attachment"
                    accept="image/jpeg,image/png"
                    onChange={handleFileChange}
                    className={`mt-1 block w-full text-sm text-gray-600 border rounded-md p-[1px] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 ${
                      errors.attachment ? "border-red-500" : "border-gray-300"
                    }`}
                    disabled={isLoading}
                  />
                  {errors.attachment && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.attachment}</p>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Payment Mode <RequiredIcon />
                  </label>
                  <DynamicSelect
                    options={paymentModeOptions}
                    value={
                      formData.paymentMode
                        ? { value: formData.paymentMode, label: formData.paymentMode }
                        : null
                    }
                    onChange={handleSelectChange("paymentMode")}
                    isSearchable={false}
                    placeholder="Select Payment Mode"
                    className={`w-full rounded-md border ${
                      errors.paymentMode ? "border-red-500" : "border-gray-300"
                    } bg-gray-50 shadow-sm`}
                    isDisabled={isLoading}
                  />
                  {errors.paymentMode && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.paymentMode}</p>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Vehicle Number <RequiredIcon />
                  </label>
                  <DynamicSelect
                    options={vehicleOptions}
                    value={
                      formData.vehicleModel
                        ? { value: formData.vehicleModel, label: formData.vehicleModel }
                        : null
                    }
                    onChange={handleSelectChange("vehicleModel")}
                    isSearchable
                    placeholder="Select Vehicle Number"
                    className={`w-full rounded-md border ${
                      errors.vehicleModel ? "border-red-500" : "border-gray-300"
                    } bg-gray-50 shadow-sm`}
                    isDisabled={isLoading}
                  />
                  {errors.vehicleModel && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.vehicleModel}</p>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Driver Name <RequiredIcon />
                  </label>
                  <input
                    name="driverName"
                    className={`w-full p-2 border rounded-md text-sm bg-gray-100 ${
                      errors.driverName ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.driverName}
                    readOnly
                    disabled
                  />
                  {errors.driverName && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.driverName}</p>
                  )}
                </div>
                <div>
                  <label className="label font-medium text-black">
                    Standard Charge (৳) <RequiredIcon />
                  </label>
                  <input
                    name="standardCharge"
                    type="number"
                    className={`w-full p-2 border rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.standardCharge ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.standardCharge}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    disabled={isLoading}
                  />
                  {errors.standardCharge && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.standardCharge}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-6 mt-6">
                <div className="flex-1">
                  <label className="label font-medium text-black">Note</label>
                  <textarea
                    name="note"
                    className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.note ? "border-red-500" : "border-gray-300"
                    }`}
                    value={formData.note}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Enter any additional notes"
                    disabled={isLoading}
                  />
                  {errors.note && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.note}</p>
                  )}
                </div>
                <div className="flex-1">
                  <table className="w-full table-fixed border-collapse ml-auto text-sm">
                    <tbody>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Payment Amount (৳) <RequiredIcon />
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            name="paymentAmount"
                            type="number"
                            className={`w-full p-2 border rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.paymentAmount ? "border-red-500" : "border-gray-300"
                            }`}
                            value={formData.paymentAmount}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            disabled={isLoading}
                          />
                          {errors.paymentAmount && (
                            <p className="text-red-500 text-xs mt-1.5">{errors.paymentAmount}</p>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Vat (%)
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            name="tax"
                            type="number"
                            className={`w-full p-2 border rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.tax ? "border-red-500" : "border-gray-300"
                            }`}
                            value={formData.tax}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            disabled={isLoading}
                          />
                          {errors.tax && (
                            <p className="text-red-500 text-xs mt-1.5">{errors.tax}</p>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Discount (%)
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            name="discount"
                            type="number"
                            className={`w-full p-2 border rounded-md text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.discount ? "border-red-500" : "border-gray-300"
                            }`}
                            value={formData.discount}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            disabled={isLoading}
                          />
                          {errors.discount && (
                            <p className="text-red-500 text-xs mt-1.5">{errors.discount}</p>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Total (৳)
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="number"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm text-right bg-gray-100"
                            value={calculatedValues.total}
                            disabled
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Discount Amount (৳)
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="number"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm text-right bg-gray-100"
                            value={calculatedValues.discountAmount}
                            disabled
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Tax Amount (৳)
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="number"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm text-right bg-gray-100"
                            value={calculatedValues.taxAmount}
                            disabled
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                          Net Amount (৳)
                        </td>
                        <td className="border border-gray-200 py-2 px-3">
                          <input
                            type="number"
                            className="w-full p-2 border border-gray-300 rounded-md text-sm text-right bg-gray-100"
                            value={calculatedValues.netAmount}
                            disabled
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {errors.apiError && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.apiError}</p>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
        <div className="flex justify-end items-center gap-3 px-6 fixed w-full bottom-0 p-3 bg-gray-200 z-20 border-t border-dashed border-M-text-color/50">
          <button
            onClick={handleReset}
            className="bg-gray-600 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
            disabled={isLoading}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAndPrint}
            className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isEditing ? "Update & Print" : "Save & Print"}
          </button>
          <button
            onClick={handleSubmit}
            className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isEditing ? "Update" : "Save"}
          </button>
        </div>
        {showPrintPreview && printData && (
          <div className="ambulance-bill-print-preview">
            <PrintBillLayout
              className="ambulance-bill-print-content"
              title="Ambulance Call Bill"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              note={printData.note}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AmbulanceCallForm;